import { BlogPostContentType } from 'constants/blog';
import { BlogPost } from 'containers/blog/post/types';

const ExplorationExploitation: BlogPost = {
  id: 'exploration',
  title: 'The exploration exploitation tradeoff',
  subtitle: 'And why I choose the former',
  previewImage:
    'https://images.unsplash.com/photo-1564772663421-c245d5f3ca93?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
  date: new Date('1 Jun 2021'),
  content: [
    {
      type: BlogPostContentType.TITLE,
      content: 'The exploration exploitation tradeoff',
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: 'And why I choose the former',
    },
    {
      type: BlogPostContentType.POST_INFO,
      content: '1 Jun 2021 · 4 minute read',
    },
    {
      type: BlogPostContentType.FULL_IMAGE,
      content:
        'https://images.unsplash.com/photo-1564772663421-c245d5f3ca93?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=2544&q=80',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'When I was little, trains got my attention. I used to go every day to the edge of the city to see them passing. By then, the thing I was most amazed by was the train itself, the engine, the wheels, the speed. Years have passed and trains still spark a curiosity within. The perspective has changed, though.',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'There\'s a great quote that says, "Never stop exploring". Simple, yet very powerful, because it illustrates my mindset ever since. Always curious and eager to learn more and explore new grounds.',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "I've just finished reading \"Algorithms to live by\" like I don't already have enough geeky stuff in my life. Besides many insightful correlations between how computers and humans work, there's one specific chapter that got my attention. It's about tradeoffs, often a hard thing to handle when you're a perfectionist. There's no free lunch, obviously, but some lunches just taste better. How do you efficiently identify them ahead of time, so you don't end fed up with regret when you figure out you've made a poor decision.",
    },
    {
      type: BlogPostContentType.SECTION_BREAK,
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "When to explore and when to exploit? There's science backing this question. The high line is that you should explore most of your early time in an experience and keep exploiting for the end. The authors explain this very straightforward. If you have limited time around, the value of exploration decays in time. Early on, the \"risk\" of exploring is worth taking, since even if you might get into a worse state, there's a high probability of finding something amazing, thus increasing the expected value. On the other hand, when the end is close, it's worth exploiting that awesome restaurant you know of, rather than exploiting and accepting the probabilities.",
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'So how does this link to travel? I find this the best means to a proper exploration load. Going to new places broadens my horizon and lets me experience new ways of living, see new cultures and meet new people.',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "It's often overwhelming when I pass people on a fancy street I've never been on. There are so many stories walking around, that I'll never get to know. So many ways of living, so many life paths, so much diversity.",
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content:
        'https://images.unsplash.com/photo-1585585835558-d80c05b82810?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "This intensifies exponentially when I'm in airports and train stations. Being international hubs, the stories here go a long way. It feels awesome to be near people from all over the world, have a nice chat with a random stranger you'll probably never see again in your life, or see how random people go through the same experiences as you do.",
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "I saw a nice image a while ago. It about reasons why people travel. A few mainstream ones, but one that got my eyes: 'Traveling for appreciating the places you left behind'. Definitely true, since sometimes things don't go so smooth, sometimes plans fail. There's an inherent risk in trying new things, but if you never try you'll never know. Not knowing that things could be better or worse makes you either under or over-appreciate the current state of life. Not having seen things with different eyes might lead you to no man's land: a place where things are not good enough to make you really happy, neither bad enough to motivate you to make a change. It's a place where I sometimes find myself, always seeking a way to change something.",
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "I always seek exploration, accepting the probabilities of going through the lowest lows only to reach the highest highs. Adventure has always sparked a fire within, be it in mountains with rough weather or in crowded cities with lots of things that can happen, life seems to go too fast to go in the same place twice or exploit the local optima too much. Without some proper exploration, you might be forever stuck on that beautiful hill, cloudy enough to prohibit you from seeing that, traversing a short valley could lead you to higher peaks with better views. The mind game to play, though, is leaving a nice spot and diving deep into the valleys of life to seek higher peaks. I'm sure they will come sooner or later, but, sometimes, the only optimal step to take is downhill.",
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content:
        'https://uploads-ssl.webflow.com/5cefd511d9c5cc7fb52f152d/5d3d646b3dfa664f77408a79_sg-dip.png',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "There's a principle in sports as well as in business that shows that going through dips brings amazing results. You can choose to scale linearly, but as science shows it, it does not go a long way. Instead, if you accept traversing the dip, the momentum gained from going downhill might result in some exponential growth later, which will pay off in the long run.",
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "I often picture experiences as slingshots. In bad times, opportunities or low-hanging fruits might knock on the door; it's just a tradeoff between accepting them immediately or push a bit more to reach higher grounds. Only then, when you launch yourself, you might be able to reach better places on the positive side. Ah, tradeoffs all around. Risk vs instant gratification at every corner, which to pick? What's the worst that can happen if you take risks, after all? Lose your stuff, go bankrupt, suffer a while, those seem terrible. But early in life, there's plenty of time to start the climb over again.",
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "I'm writing these thoughts early in the morning, on a train towards some unknown places and new adventures. Life moves so fast, it's a pity to just let it go. Being involved and seeking exploration feels great so far. Such a nice world to experience.",
    },
    {
      type: BlogPostContentType.TEXT,
      content: 'I guess the best is yet to come. Mihai.',
    },
  ],
};

export default ExplorationExploitation;
